import { type ReactNode, useMemo, useState } from 'react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import type {
  Control,
  FieldErrors,
  UseFormHandleSubmit,
} from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import type {
  OrderRequestedShipping,
  OrderShippingLabel,
} from '@skupreme/api-contracts/core';
import { Card } from '@skupreme/shared/ui';
import { CommonUtility, type Address } from '@utility/common';
import type { ConnectedCarrierAccounts } from '@utility/own-carrier-account';
import type {
  CurrentOrganization,
  FulfillmentProviderOption,
} from '../../legacy-bridge';
import {
  buildRateOptions,
  cheapestRateKey,
  rateOptionKey,
  type ProviderQuote,
  type RateOption,
} from '../../lib/rates';
import { formatAddress } from '../../lib/address';
import {
  isOpen,
  shipmentTitle,
  type ShipmentStatus,
} from '../../lib/shipments';
import type {
  FulfillmentItem,
  FulfillmentProviderSelection,
  ShippingFormValues,
} from '../../lib/shipping-form';
import type { PackageBox } from '../../lib/packages';
import { FulfillmentCard } from './FulfillmentCard';
import { PackagesCard } from './PackagesCard';
import { FulfillButtons } from './ShipmentParts';
import { ShipmentSection, type ShipmentDdp } from './ShipmentSection';
import {
  ShipmentCard,
  UnstockedItemsCard,
  type ItemDestination,
} from './ShipmentCard';
import {
  countShippedShipments,
  ShipmentSummaryList,
  type ShipmentSummaryOrder,
  type TrackingStatus,
} from './ShipmentSummaryList';
import type { LegacyOption } from './fields';

/** A planned shipment with everything the card needs to draw itself. */
export type ShipmentView = {
  id: string;
  providerId: string;
  providerName?: string;
  items: FulfillmentItem[];
  status: ShipmentStatus;
  error?: string;
};

export type FulfillmentColumnProps = {
  control: Control<ShippingFormValues>;
  errors: FieldErrors<ShippingFormValues>;

  /** One card each, in the order they will be bought. */
  shipments: ShipmentView[];
  shipmentsLoading?: boolean;
  /**
   * The shipments the order has already made. They head the same list as the
   * planned ones, because an order that ships in pieces is still one order, and
   * a label already bought is the part of it that is settled.
   */
  order?: ShipmentSummaryOrder;
  labels?: OrderShippingLabel[];
  returnLabels?: OrderShippingLabel[];
  labelsLoading?: boolean;
  trackingStatuses?: TrackingStatus[];
  onViewLabel?: (label: OrderShippingLabel) => void;
  onRefundLabel?: (label: OrderShippingLabel) => Promise<void>;
  /** The shipment the form is bound to; only it can be filled in and bought. */
  activeShipmentId?: string;
  /** Owed lines no warehouse has stock for, so no shipment carries them. */
  unstockedItems?: FulfillmentItem[];
  onMoveItem: (itemId: string, targetShipmentId: string) => void;
  onSplitItem: (itemId: string) => void;
  /** A queued shipment can change warehouse without holding the form. */
  onShipmentProviderChange: (shipmentId: string, providerId: string) => void;
  /** Moves the shipping options to a queued shipment, to buy that one first. */
  onActivateShipment: (shipmentId: string) => void;
  onMergeShipments: () => void;
  /** Whether the warehouse behind a shipment stocks the line being moved. */
  isItemAvailableAt: (itemId: string, providerId: string) => boolean;
  /** Opens the catalog match for an owed line with no catalog item. */
  onMatchItem?: (itemId: string) => void;
  /** What the operator does about the owed lines, under them. */
  unstockedNote?: string;
  /** A line under the active shipment's warehouse. */
  shipFromHint?: string;
  /**
   * EN-4544 (V2): the main button sends the shipment to its warehouse, which
   * needs no quote: the speed and signature inputs go, a warehouse that quotes
   * nothing is not an error, and its button matches the routing card's.
   */
  sendsToProvider?: boolean;

  fpList: FulfillmentProviderOption[];
  fpLoading?: boolean;
  fpErrorMessage?: string;
  providerRates: Record<string, ProviderQuote>;
  currentProvider?: {
    address?: Address;
    isFBA?: boolean;
    showOutOfStockBadge?: boolean;
  };
  fulfillmentProviderChanged: (item: {
    value?: FulfillmentProviderSelection;
  }) => void;
  /** Picking a service leaves the warehouse alone, so only the tuple is rewritten. */
  rateSelected: (selection: FulfillmentProviderSelection | null) => void;

  /** The boxes the order ships in, as the warehouse quoted them. */
  packageBoxes?: PackageBox[];
  currentOrg?: CurrentOrganization;
  /** The workspace's own carrier accounts, to mark the rates they quote. */
  connectedAccounts?: ConnectedCarrierAccounts;

  enableAutoPrint?: boolean;
  customOrigins?: { name: string; primary?: boolean }[];
  customOriginLoading?: boolean;

  providers: LegacyOption[];
  services: LegacyOption[];
  packages: LegacyOption[];
  contentTypes: LegacyOption[];
  shippingSpeedData: LegacyOption[];
  /** The marketplace's own instruction, shown under the speed it defaults to. */
  requestedShipping?: OrderRequestedShipping | null;
  metadataLoading?: boolean;
  providerChanged: (item: { value: string }) => void;
  serviceChanged: (item: { value: string }) => void;
  show: { dimensions: boolean; weight: boolean };
  /** Above the weight and size of a label: the cartons they edit. */
  cartons?: ReactNode;

  /**
   * False while a manual provider is selected, which is when we buy the label.
   * Undefined until a warehouse is known, and the shipping form waits it out.
   */
  isNotManualFP?: boolean;
  isUSPS?: boolean;
  /** USPS refuses hazmat, so the buy button stays shut for that pair. */
  isHazmat?: boolean;
  isInternational?: boolean;
  isRFCRequired?: boolean;
  isSignature?: boolean;
  setIsSignature: (value: boolean) => void;

  rate?: number | null;
  rateCurrency?: string;
  rateError?: string;
  rateLoading?: boolean;
  rateNegotiate?: boolean;
  /** A caption under the rate, e.g. that it is an estimate. */
  rateNote?: string;
  time?: string | null;
  ddp?: ShipmentDdp | null;

  disabledCreateLabel?: boolean;
  disabledFulfillment?: boolean;
  saving?: boolean | string;
  batchOrder?: boolean;
  invalidAddress?: boolean;
  orderError?: string;
  addressValidationClick: () => void;
  autoFulfillerClicked: (testMode?: boolean) => void;
  /** Absent unless a fulfill queue has somewhere left to go. */
  fulfillAndNextClicked?: () => void;
  createLabel: (data: ShippingFormValues, testMode: boolean) => void;
  handleSubmit: UseFormHandleSubmit<ShippingFormValues>;
  forceGeneratePublicDimension?: () => void;
  uploadShippingLabel?: () => void;
  modeControl?: ReactNode;
};

function inventoryText(items: { available?: number }[] = []) {
  const quantities = items
    .map(item => Number(item.available))
    .filter(Number.isFinite);

  if (quantities.length === 0) {
    return undefined;
  }
  if (quantities.some(quantity => quantity === 999999)) {
    return 'In stock';
  }

  const available = quantities.reduce((total, quantity) => total + quantity, 0);
  return `${available} available`;
}

export function shipmentNeedsExplicitRelabelRate(
  shipment: ShipmentView | undefined,
  labels: OrderShippingLabel[] = [],
) {
  const itemIds = new Set(
    (shipment?.items || []).map(item => String(item._id)),
  );

  if (itemIds.size === 0) {
    return false;
  }

  return labels.some(
    label =>
      label.refunded === true &&
      label.mode !== 'test' &&
      (label.items || []).some(item =>
        [item.item_id, item.bundle_item_id].some(
          itemId => itemId && itemIds.has(String(itemId)),
        ),
      ),
  );
}

export function hasEligibleExplicitRate(
  selection: FulfillmentProviderSelection | undefined,
  rateOptions: Pick<RateOption, 'selection' | 'isDisabled'>[],
) {
  return rateOptions.some(
    option =>
      !option.isDisabled &&
      selection?.id === option.selection.id &&
      selection.provider === option.selection.provider &&
      selection.service === option.selection.service &&
      selection.packageValue === option.selection.packageValue,
  );
}

/**
 * The fulfillment side of the details page: one card per shipment, each with
 * the lines it carries, the warehouse it ships from and its own buy action.
 * It owns no form state — every field is bound to the caller's form.
 */
export function FulfillmentColumn({
  control,
  errors,
  shipments = [],
  shipmentsLoading,
  activeShipmentId,
  unstockedItems = [],
  order,
  labels = [],
  returnLabels = [],
  labelsLoading,
  trackingStatuses = [],
  onViewLabel,
  onRefundLabel,
  onMoveItem,
  onSplitItem,
  onShipmentProviderChange,
  onActivateShipment,
  onMergeShipments,
  isItemAvailableAt,
  onMatchItem,
  unstockedNote,
  shipFromHint,
  sendsToProvider,
  fpList = [],
  fpLoading,
  fpErrorMessage,
  providerRates = {},
  currentProvider,
  fulfillmentProviderChanged,
  rateSelected,
  packageBoxes = [],
  currentOrg,
  connectedAccounts,
  enableAutoPrint,
  customOrigins = [],
  customOriginLoading,
  providers = [],
  services = [],
  packages = [],
  contentTypes = [],
  shippingSpeedData = [],
  requestedShipping,
  metadataLoading,
  providerChanged,
  serviceChanged,
  show,
  cartons,
  isNotManualFP,
  isUSPS,
  isHazmat,
  isInternational,
  isRFCRequired,
  isSignature,
  setIsSignature,
  rate,
  rateCurrency,
  rateError,
  rateLoading,
  rateNegotiate,
  rateNote,
  time,
  ddp,
  disabledCreateLabel,
  disabledFulfillment,
  saving,
  batchOrder,
  invalidAddress,
  orderError,
  addressValidationClick,
  autoFulfillerClicked,
  fulfillAndNextClicked,
  createLabel,
  handleSubmit,
  forceGeneratePublicDimension,
  uploadShippingLabel,
  modeControl,
}: FulfillmentColumnProps) {
  const selection = useWatch({ control, name: 'fulfillment_provider_id' });
  const activeShipment = shipments.find(
    shipment => shipment.id === activeShipmentId,
  );
  const selectedRateKey = selection ? rateOptionKey(selection) : 'any';
  const requiresExplicitRelabelRate = shipmentNeedsExplicitRelabelRate(
    activeShipment,
    labels,
  );

  const providerList = useMemo(
    () =>
      fpList.map(provider => ({
        value: provider.value,
        label: provider.text,
        description: formatAddress(provider.item?.address),
        availability: provider.item?.showOutOfStockBadge
          ? 'Out of stock'
          : inventoryText(provider.item?.items),
        isOutOfStock: provider.item?.showOutOfStockBadge,
        logoUrl: provider.logoUrl,
      })),
    [fpList],
  );

  /* Only the warehouse the open shipment ships from is quoted, so only its
     services are on offer. The shipment is asked rather than the form: a
     remounted field can leave the form holding nothing to price. */
  const shipFromId =
    shipments.find(shipment => shipment.id === activeShipmentId)?.providerId ||
    selection?.id;
  const quote = shipFromId ? providerRates[shipFromId] : undefined;
  const rateOptions = useMemo(
    () =>
      shipFromId
        ? buildRateOptions(shipFromId, quote, undefined, connectedAccounts)
        : [],
    [shipFromId, quote, connectedAccounts],
  );
  const hasEligibleRelabelRate = hasEligibleExplicitRate(
    selection,
    rateOptions,
  );

  /* An origin typed in by hand joins the ones the org has saved, and is kept
     apart from them so a reload of the saved list does not drop it. */
  const [typedOrigins, setTypedOrigins] = useState<string[]>([]);
  const customOriginOptions = useMemo(() => {
    const names = new Set([
      ...customOrigins.map(origin => origin.name),
      ...typedOrigins,
    ]);

    return [...names].map(name => ({ text: name, key: name, value: name }));
  }, [customOrigins, typedOrigins]);

  const addCustomOrigin = (_: unknown, { value }: { value?: string }) => {
    if (!value) {
      return;
    }
    setTypedOrigins(names =>
      names.includes(value) ? names : [...names, value],
    );
  };

  const isBusy = !!saving;
  const isProviderPending = isNotManualFP === undefined;

  /* Fulfilling for real costs money, so a 3PL test run needs the org's opt-in. */
  const canTestMode = useMemo(
    () =>
      !CommonUtility.isProductionEnv() &&
      (!isNotManualFP || !!currentOrg?.test),
    [isNotManualFP, currentOrg],
  );

  /* Numbered across both halves of the list, so the second shipment of an
     order reads as the second whether it has shipped or is still being paid for. */
  const shippedCount = useMemo(
    () => countShippedShipments(order, labels),
    [order, labels],
  );
  const shipmentCount = shippedCount + shipments.length;
  /* EN-4544: a V2 provider send takes every shipment at once. */
  const sendsAll = !!sendsToProvider && shipments.length > 1;

  /* Named by warehouse rather than by number: what the shipment is called
     changes as shipments come and go, but where it ships from does not. */
  const destinations = (fromShipmentId?: string) => (itemId: string) =>
    shipments
      .map((shipment, index) => ({ shipment, index }))
      .filter(
        ({ shipment }) => isOpen(shipment) && shipment.id !== fromShipmentId,
      )
      .map<ItemDestination>(({ shipment, index }) => ({
        id: shipment.id,
        label:
          shipment.providerName ||
          shipmentTitle(shippedCount + index, shipmentCount),
        isAvailable: isItemAvailableAt(itemId, shipment.providerId),
      }));

  if (shipmentsLoading) {
    return (
      <Card title="Shipping">
        <span
          role="status"
          aria-label="Preparing shipment options"
          className="sk:flex sk:items-center sk:gap-2 sk:text-dense sk:text-fg-secondary"
        >
          <CircleNotchIcon size={14} className="sk:animate-spin" />
          Preparing shipment options
        </span>
      </Card>
    );
  }

  return (
    <div className="sk:flex sk:flex-col sk:gap-4">
      {onViewLabel ? (
        <ShipmentSummaryList
          order={order}
          labels={labels}
          returnLabels={returnLabels}
          isLoading={labelsLoading}
          trackingStatuses={trackingStatuses}
          numbering={{ offset: 0, total: shipmentCount }}
          onViewLabel={onViewLabel}
          onRefundLabel={onRefundLabel}
        />
      ) : null}

      {shipments.map((shipment, index) => {
        const isActive = shipment.id === activeShipmentId;

        return (
          <ShipmentCard
            key={shipment.id}
            title={shipmentTitle(shippedCount + index, shipmentCount)}
            items={shipment.items}
            status={shipment.status}
            error={shipment.error}
            isActive={isActive}
            /* One shipment goes out at a time, so nothing moves while it does. */
            isLocked={isBusy}
            providerId={shipment.providerId}
            providerName={shipment.providerName}
            providerOptions={providerList}
            onProviderChange={id => onShipmentProviderChange(shipment.id, id)}
            /* A provider send takes every shipment at once, so none goes first. */
            onActivate={
              isActive || sendsToProvider
                ? undefined
                : () => onActivateShipment(shipment.id)
            }
            isInternational={isInternational}
            isLoading={fpLoading}
            /* The boxes the quote priced, so the count and the price are the
               same answer about the same lines. */
            packageCount={isActive ? packageBoxes.length : 0}
            canCustomizePackaging={!!isNotManualFP && !currentProvider?.isFBA}
            onCustomizePackaging={forceGeneratePublicDimension}
            onAttachLabel={uploadShippingLabel}
            destinationsFor={destinations(shipment.id)}
            onMoveItem={onMoveItem}
            onSplitItem={onSplitItem}
            onMergeAll={
              shipments.filter(isOpen).length > 1 ? onMergeShipments : undefined
            }
          >
            {isActive ? (
              <FulfillmentCard
                control={control}
                errors={errors}
                providers={providerList}
                isLoading={fpLoading}
                currentProvider={currentProvider}
                quoteError={sendsToProvider ? undefined : quote?.fetchRateError}
                providerError={fpErrorMessage}
                rateError={isNotManualFP ? undefined : rateError}
                onProviderChange={fulfillmentProviderChanged}
                showQuoteInputs={!!isNotManualFP && !sendsToProvider}
                isProviderPending={isProviderPending}
                shippingSpeeds={shippingSpeedData}
                requestedShipping={requestedShipping}
                isSignature={isSignature}
                onSignatureChange={setIsSignature}
                enableAutoPrint={enableAutoPrint}
                customOriginOptions={customOriginOptions}
                customOriginLoading={customOriginLoading}
                onAddCustomOrigin={addCustomOrigin}
                modeControl={modeControl}
                shipFromHint={shipFromHint}
                packagesSection={
                  isNotManualFP &&
                  !currentProvider?.isFBA &&
                  !(sendsToProvider && quote?.fetchRateError) ? (
                    <PackagesCard
                      boxes={packageBoxes}
                      isLoading={quote?.loading}
                      unavailableReason={quote?.fetchRateError}
                    />
                  ) : null
                }
              >
                <ShipmentSection
                  control={control}
                  errors={errors}
                  isManual={!isNotManualFP}
                  isProviderPending={isProviderPending}
                  providers={providers}
                  services={services}
                  packages={packages}
                  contentTypes={contentTypes}
                  metadataLoading={metadataLoading}
                  show={show}
                  cartons={cartons}
                  isUSPS={isUSPS}
                  isInternational={isInternational}
                  isRFCRequired={isRFCRequired}
                  onCarrierChange={value => providerChanged({ value })}
                  onServiceChange={value => serviceChanged({ value })}
                  rate={rate}
                  rateCurrency={rateCurrency}
                  rateError={rateError}
                  rateLoading={rateLoading}
                  rateNegotiate={rateNegotiate}
                  rateNote={rateNote}
                  time={time}
                  ddp={ddp}
                  providerRate={quote?.rate}
                  rateOptions={rateOptions}
                  selectedRateKey={selectedRateKey}
                  cheapestRateKey={cheapestRateKey(rateOptions)}
                  ratesLoading={quote?.loading}
                  onSelectRate={option =>
                    rateSelected(option?.selection || null)
                  }
                  orgName={currentOrg?.company_name}
                  orgLogo={currentOrg?.logo?.url}
                  allowAutomaticRate={!requiresExplicitRelabelRate}
                  isSubmitDisabled={
                    disabledCreateLabel || isBusy || (!!isUSPS && !!isHazmat)
                  }
                  isFulfillmentDisabled={
                    disabledFulfillment ||
                    labelsLoading ||
                    (requiresExplicitRelabelRate && !hasEligibleRelabelRate)
                  }
                  isBusy={isBusy}
                  batchOrder={batchOrder}
                  allowTestMode={canTestMode}
                  invalidAddress={invalidAddress}
                  onValidateAddress={addressValidationClick}
                  onFulfill={testMode => autoFulfillerClicked(testMode)}
                  onFulfillAndNext={fulfillAndNextClicked}
                  onCreateTestLabel={handleSubmit(data =>
                    createLabel(data, true),
                  )}
                  orderError={orderError}
                  sendsToProvider={sendsToProvider}
                  hidesFulfill={sendsAll}
                />
              </FulfillmentCard>
            ) : null}
          </ShipmentCard>
        );
      })}

      {/* One press sends every shipment to its warehouse, so the action is the order's, under them all. */}
      {sendsAll ? (
        <FulfillButtons
          label={`Fulfill ${shipments.length} Shipments`}
          isDisabled={
            disabledFulfillment || labelsLoading || isBusy || invalidAddress
          }
          onFulfill={() => autoFulfillerClicked()}
          onFulfillAndNext={fulfillAndNextClicked}
        />
      ) : null}

      {/* Silent only when the order has nothing at all to show here. */}
      {shipmentCount === 0 && unstockedItems.length === 0 && !fpLoading ? (
        <span className="sk:py-3 sk:text-dense sk:text-fg-tertiary">
          Nothing can be fulfilled from stock right now.
        </span>
      ) : null}

      <UnstockedItemsCard
        items={unstockedItems}
        destinationsFor={destinations()}
        isLocked={isBusy}
        onMoveItem={onMoveItem}
        onSplitItem={onSplitItem}
        onMatchItem={onMatchItem}
        note={unstockedNote}
      />
    </div>
  );
}
